// conectagran/frontend/src/pages/AdManager/GamDashboard.tsx
import { useState, useMemo } from 'react'
import { RefreshCw, Unplug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { GamChart } from './GamChart'
import { GamTable } from './GamTable'
import { GamUrlTable } from './GamUrlTable'
import {
  useGamConnection,
  useGamReport,
  useGamUrlReport,
  useSyncGam,
  useDeleteGamConnection,
} from '@/api/hooks/useGam'
import type { GamDimension } from '@/api/hooks/useGam'

type PeriodKey = 'today' | '24h' | '2d' | '7d' | '30d' | '90d' | '365d'

const PERIODS: { label: string; key: PeriodKey }[] = [
  { label: 'Hoje',     key: 'today'  },
  { label: '24h',      key: '24h'    },
  { label: '2 dias',   key: '2d'     },
  { label: '7 dias',   key: '7d'     },
  { label: '30 dias',  key: '30d'    },
  { label: '90 dias',  key: '90d'    },
  { label: '365 dias', key: '365d'   },
]

// GAM network timezone = America/Sao_Paulo (BRT). Snapshots are stored in BRT.
// We compute the current BRT date + hour using Intl so that the calendar is
// aligned with what the GAM UI shows, regardless of the browser's local TZ.
function getBrtNow(): { date: string; hour: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hour12: false,
  })
  const parts = fmt.formatToParts(new Date())
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '0'
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: parseInt(get('hour'), 10) % 24, // Intl may return "24" at midnight in some runtimes
  }
}

function addDaysToIsoDate(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

interface ReportParams {
  from:      string
  to:        string
  fromHour?: number
  toHour?:   number
}

function buildReportParams(period: PeriodKey): ReportParams {
  const { date: today, hour } = getBrtNow()

  switch (period) {
    case 'today':
      // 00:00 → current hour BRT, same calendar day
      return { from: today, to: today, fromHour: 0, toHour: hour }

    case '24h': {
      // Rolling 24h window ending at current BRT hour.
      // Edge case: at midnight (hour=0), "last 24h" = yesterday 00:00 → yesterday 23:00.
      if (hour === 0) {
        const yesterday = addDaysToIsoDate(today, -1)
        return { from: yesterday, to: yesterday, fromHour: 0, toHour: 23 }
      }
      // Otherwise: yesterday[currentHour..23] + today[0..currentHour-1] = 24 hours.
      const yesterday = addDaysToIsoDate(today, -1)
      return { from: yesterday, to: today, fromHour: hour, toHour: hour - 1 }
    }

    case '2d':   return { from: addDaysToIsoDate(today, -1),   to: today }
    case '7d':   return { from: addDaysToIsoDate(today, -6),   to: today }
    case '30d':  return { from: addDaysToIsoDate(today, -29),  to: today }
    case '90d':  return { from: addDaysToIsoDate(today, -89),  to: today }
    case '365d': return { from: addDaysToIsoDate(today, -364), to: today }
  }
}

export function GamDashboard() {
  const [period, setPeriod] = useState<PeriodKey>('today')
  const [dimension, setDimension] = useState<GamDimension | 'url'>('hour')
  const [refreshKey, setRefreshKey] = useState(0)

  // Recalcula params quando período, hora BRT, ou refreshKey mudam
  const currentBrtHour = getBrtNow().hour
  const reportParams = useMemo(() => buildReportParams(period), [period, currentBrtHour, refreshKey])

  const isUrlMode = dimension === 'url'

  const { data: reportData, isLoading } = useGamReport({
    from:     reportParams.from,
    to:       reportParams.to,
    fromHour: reportParams.fromHour,
    toHour:   reportParams.toHour,
    dimension: isUrlMode ? 'hour' : dimension as GamDimension,
    enabled: !isUrlMode,
  })

  const { data: urlData, isLoading: urlLoading } = useGamUrlReport({
    from: reportParams.from,
    to:   reportParams.to,
    enabled: isUrlMode,
  })

  const { data: connData } = useGamConnection()
  const sync = useSyncGam()
  const disconnect = useDeleteGamConnection()

  function handleDisconnect() {
    if (!confirm(`Desconectar a conta GAM (network ${connData?.network_code ?? '—'})? Você poderá reconectar a conta correta em seguida.`)) return
    disconnect.mutate(undefined, {
      onSuccess: () => { toast.success('GAM desconectado. Reconecte a conta correta.'); window.location.replace('/ad-manager') },
      onError: () => toast.error('Erro ao desconectar.'),
    })
  }

  async function handleSync() {
    try {
      await sync.mutateAsync()
      toast.success('Sync iniciada — dados atualizam em ~30s.')
      // Refetch após 30s para pegar dados novos do job
      setTimeout(() => setRefreshKey(k => k + 1), 30_000)
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Erro ao iniciar sync')
    }
  }

  const summary    = reportData?.summary
  const rows       = reportData?.rows ?? []
  const chart      = reportData?.chart ?? []
  const usdBrlRate = reportData?.usd_brl_rate

  return (
    <div className="min-h-screen bg-[#0d0f16] text-[#e2e8f0]">
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="border-b border-[#1e2235] px-5 py-3 flex items-center gap-3 flex-wrap">
        <h1 className="text-base font-bold text-[#f8fafc] tracking-tight mr-2">Ad Manager</h1>
        {connData?.network_code && (
          <span className="text-[11px] text-[#64748b] bg-[#1a1d2a] rounded px-2 py-1 font-mono">
            Network: {connData.network_code}
          </span>
        )}

        <div className="flex gap-0.5 bg-[#1a1d2a] rounded-md p-0.5 ml-auto">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                period === p.key
                  ? 'bg-[#c9a84c] text-black'
                  : 'text-[#64748b] hover:text-[#94a3b8]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <Button
          size="sm"
          variant="ghost"
          className="text-[#64748b] hover:text-[#94a3b8] gap-1.5"
          onClick={handleSync}
          disabled={sync.isPending}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />
          Sincronizar
        </Button>

        <Button
          size="sm"
          variant="ghost"
          className="text-[#ef4444] hover:text-[#f87171] gap-1.5"
          onClick={handleDisconnect}
          disabled={disconnect.isPending}
        >
          <Unplug className="w-3.5 h-3.5" />
          Desconectar
        </Button>
      </div>

      {/* ── KPI cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-5 gap-px bg-[#1e2235] border-b border-[#1e2235]">
        {[
          {
            label: 'Impressões',
            value: summary ? (summary.impressions >= 1_000_000 ? `${(summary.impressions / 1_000_000).toFixed(2)}M` : summary.impressions >= 1000 ? `${(summary.impressions / 1000).toFixed(1)}K` : summary.impressions.toString()) : '—',
            sub:   summary && summary.unfilled > 0 ? `${summary.unfilled.toLocaleString('pt-BR')} não preenchidas` : undefined,
            delta: summary?.delta_impressions ?? null,
          },
          {
            label: 'Fill Rate',
            value: summary ? `${summary.fill_rate.toFixed(1)}%` : '—',
            sub:   summary ? `${summary.code_served.toLocaleString('pt-BR')} requests` : undefined,
            delta: summary?.delta_fill_rate ?? null,
          },
          {
            label: 'Receita',
            value: summary ? `$${summary.revenue_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—',
            sub:   summary ? `R$ ${summary.revenue_brl.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : undefined,
            delta: summary?.delta_revenue ?? null,
          },
          {
            label: 'CTR',
            value: summary ? `${summary.ctr.toFixed(2)}%` : '—',
            delta: summary?.delta_ctr ?? null,
          },
          {
            label: 'eCPM',
            value: summary ? `$${summary.ecpm.toFixed(2)}` : '—',
            delta: summary?.delta_ecpm ?? null,
          },
        ].map((kpi, i) => (
          <div key={i} className="bg-[#0d0f16] px-5 py-4">
            <div className="text-[11px] font-medium text-[#64748b] uppercase tracking-widest mb-1.5">{kpi.label}</div>
            <div className="text-[26px] font-bold text-[#f8fafc] leading-none tracking-tight">{kpi.value}</div>
            <div className="flex items-center gap-2 mt-2">
              {kpi.sub && <span className="text-xs text-[#94a3b8]">{kpi.sub}</span>}
              {kpi.delta !== null && summary?.delta_period && (
                <span
                  title={`vs ${summary.delta_period}`}
                  className={`text-xs font-semibold cursor-default ${kpi.delta >= 0 ? 'text-[#4ade80]' : 'text-[#f87171]'}`}
                >
                  {kpi.delta >= 0 ? '▲' : '▼'} {Math.abs(kpi.delta).toFixed(1)}%
                  <span className="text-[11px] font-normal text-[#475569] ml-1">vs {summary.delta_period}</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Today + Peak sub-bar ─────────────────────────────────────────── */}
      {summary && (
        <div className="flex items-center gap-6 px-5 py-3 border-b border-[#1e2235] bg-[#0b0d14]">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-[#64748b] uppercase tracking-widest">Ganhos hoje</span>
            <span className="text-[15px] font-bold text-[#f8fafc]">
              ${summary.today_revenue_usd.toFixed(2)}
              <span className="text-[#94a3b8] font-normal ml-2 text-[13px]">≈ R$ {summary.today_revenue_brl.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</span>
            </span>
          </div>
          <div className="w-px h-8 bg-[#1e2235]" />
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-[#64748b] uppercase tracking-widest">Pico eCPM</span>
            <span className="text-[15px] font-bold text-[#c9a84c]">
              ${summary.peak_ecpm.toFixed(2)}
              {summary.peak_label && <span className="text-[#94a3b8] font-normal ml-2 text-[13px]">às {summary.peak_label}</span>}
            </span>
          </div>
          {usdBrlRate && (
            <>
              <div className="w-px h-8 bg-[#1e2235]" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[11px] font-medium text-[#64748b] uppercase tracking-widest">Cotação USD</span>
                <span className="text-[15px] font-bold text-[#f8fafc]">R$ {usdBrlRate.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Chart ────────────────────────────────────────────────────────── */}
      {(dimension !== 'ad_unit' && dimension !== 'finance_page' && dimension !== 'device') && <div className="px-5 py-4 border-b border-[#1e2235]">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-[#cbd5e1]">
            eCPM ao longo do período
          </span>
          <div className="flex items-center gap-1.5 text-xs text-[#94a3b8]">
            <div className="w-2.5 h-2.5 rounded-full bg-[#c9a84c]" />
            <span>eCPM (USD)</span>
          </div>
        </div>
        {isLoading ? (
          <div className="h-[320px] flex items-center justify-center text-[#475569] text-sm">
            Carregando...
          </div>
        ) : (
          <GamChart data={chart} />
        )}
      </div>}

      {/* ── Dimension selector ───────────────────────────────────────────── */}
      <div className="px-5 py-2 border-b border-[#1e2235] bg-[#0b0d14] flex items-center gap-2">
        <span className="text-xs font-medium text-[#64748b]">Quebrar por:</span>
        {([
          { key: 'hour',         label: 'Hora do dia'   },
          { key: 'day',          label: 'Dia'           },
          { key: 'url',          label: 'Por URL'       },
          { key: 'finance_page', label: 'Por link'      },
          { key: 'ad_unit',      label: 'Ad unit'       },
          { key: 'device',       label: 'Dispositivo'   },
        ] as const).map(d => (
          <button
            key={d.key}
            onClick={() => setDimension(d.key)}
            className={`px-3 py-1 rounded text-xs border transition-colors ${
              dimension === d.key
                ? 'border-[#c9a84c] text-[#c9a84c] bg-[#c9a84c12]'
                : 'border-[#252836] text-[#94a3b8] bg-[#1a1d2a] hover:text-[#cbd5e1] hover:border-[#3a3d4e]'
            }`}
          >
            {d.label}
          </button>
        ))}
        <button
          onClick={handleSync}
          disabled={sync.isPending}
          className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold border border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c08] hover:bg-[#c9a84c18] hover:border-[#c9a84c]/60 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${sync.isPending ? 'animate-spin' : ''}`} />
          {sync.isPending ? 'Atualizando...' : 'Gerar relatório atualizado'}
        </button>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      {isUrlMode ? (
        <GamUrlTable data={urlData} isLoading={urlLoading} />
      ) : isLoading ? (
        <div className="p-8 text-center text-[#64748b] text-sm">Carregando...</div>
      ) : (
        <GamTable rows={rows} />
      )}

      {/* ── Footnote ─────────────────────────────────────────────────────── */}
      <div className="px-5 py-2.5 text-[11px] text-[#475569] border-t border-[#1e2235]">
        ⚠ Dados com delay de ~3h (sync a cada 3h).{' '}
        {(reportData?.last_synced_at || connData?.last_synced_at)
          ? `Última atualização: ${new Date((reportData?.last_synced_at || connData?.last_synced_at)!).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}`
          : 'Ainda não sincronizado.'}
      </div>
    </div>
  )
}
